import * as React from "react";

import { type AgentThreadToolResultContent } from "@/shared/domain/agentThread";

import { RUN_PYTHON_OUTCOME_LOOK } from "./runPythonLook";
import {
  describeRunPythonPill,
  detectRunPythonPayload,
  extractRunPythonArguments,
  formatSeconds,
  summarizeRunPythonCall,
} from "./runPythonPayload";
import styles from "./RunPythonPillLabel.module.css";

/** A result's `payload`, parsed: a result truncated for the model is persisted as JSON text. */
function parsePayload(payload: unknown): unknown {
  if (typeof payload !== "string") {
    return payload;
  }
  try {
    return JSON.parse(payload) as unknown;
  } catch {
    return payload;
  }
}

interface RunPythonPillLabelProps {
  toolResult: Pick<
    AgentThreadToolResultContent,
    "payload" | "status" | "runtime_ms"
  >;
  /** The call's input, for its `timeout_s`; absent when the caller has only the result. */
  toolInput?: unknown;
}

/**
 * A finished `run_python` call's pill text, with the card's status: "Ran Python · ✓ 14.3 s". A
 * result without a payload (older threads) shows only the call and its time.
 */
export function RunPythonPillLabel({
  toolResult,
  toolInput,
}: RunPythonPillLabelProps): React.JSX.Element {
  if (toolResult.payload === undefined || toolResult.payload === null) {
    return (
      <>
        <b>Ran Python</b> · {formatSeconds(toolResult.runtime_ms / 1000)}
      </>
    );
  }
  const summary = summarizeRunPythonCall({
    payload: detectRunPythonPayload(parsePayload(toolResult.payload)),
    toolSucceeded: toolResult.status === "success",
    runtimeMs: toolResult.runtime_ms,
    timeoutS: extractRunPythonArguments(toolInput).timeoutS,
  });
  const { icon: Icon, tone } = RUN_PYTHON_OUTCOME_LOOK[summary.outcome.kind];
  return (
    <>
      <b>Ran Python</b> ·{" "}
      <span className={styles.outcome} data-tone={tone}>
        <Icon aria-hidden className={styles.icon} />
        {describeRunPythonPill(summary)}
      </span>
    </>
  );
}
